// Order/OrderDetail.tsx
import React, {useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {axios} from "Auth/axiosConfig";
import CircularProgress from "Core/components/elements/CircularProgress";
import {useErrorProcessing} from "Core/components/ErrorProvider";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import copyToClipboard from "../../Utils/clipboard";
import {communicationTypeMapping} from "Locale/ruMap";
import moment from "moment";
import ConsultationsWrapper from "Consultation/ConsultationsWrapper";
import OrderStatus from "Order/OrderStatus";
import OrderActions from "Order/OrderActions";
import {AuthContext, AuthContextType} from "Auth/AuthContext";
import {useTheme} from "Theme/ThemeContext";
import pprint from '../../Utils/pprint';
import {FC, FCCC, FR, FRSC} from "WideLayout/Layouts";
import {IOrder} from "Types/commerce/shop";

interface OrderDetailProps {
    className?: string;
}

const OrderDetail: React.FC<OrderDetailProps> = ({className}) => {
    const {id} = useParams<{ id: string }>();
    const {isAuthenticated} = useContext(AuthContext) as AuthContextType;
    const {byResponse, notAuthentication} = useErrorProcessing();
    const [order, setOrder] = useState<IOrder | null>(null);
    const [orderNotFound, setOrderNotFound] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
    const {plt} = useTheme();

    useEffect(() => {
        if (!isAuthenticated) {
            notAuthentication();
            return;
        }
        setLoading(true);
        axios.get(`/api/v1/orders/${id}/`)
            .then(response => {
                pprint(`Order ID: ${id}`, response.data);
                setOrder(response.data);
                if (!response.data) setOrderNotFound(true);
            })
            .catch(error => byResponse(error))
            .finally(() => setLoading(false));
    }, [id, isAuthenticated, notAuthentication, byResponse]);

    if (loading) return <CircularProgress size={'150px'}/>;
    if (orderNotFound || !order) return <div className={'p-3 text-center mt-2'}>Заказ не найден.</div>;

    return (
        <div className={`${className} py-3 overflow-y-auto no-scrollbar position-relative`}>
            <div className={'fc gap-2'}>
                <div className={'fc gap-1'}>
                    <div className={'frsc gap-2'} onClick={() => copyToClipboard(String(order.id))}>
                        <span className={`fs-6`} style={{color: plt.text.primary50}}>
                            # {order.id}
                        </span>
                        <ContentCopyIcon className={'fs-5'}/>
                    </div>
                    <div className={'frbc flex-wrap'}>
                        <FRSC wrap pr={1} g={1} mt={1}>
                            <FR cls={`fs-5 text-nowrap`} px={1} rounded={3}
                                bg={plt.colors.secondary.lighter}
                                color={plt.text.primary80}>
                                {order.product.polymorphic_ctype.name}
                            </FR>
                            <FR cls={`fs-5 text-nowrap`} color={plt.text.primary80}>
                                {order.product.name}
                            </FR>
                        </FRSC>
                        <span className={`fs-5 fw-3 mt-2`}
                              style={{
                                  color: plt.text.primary80,
                                  lineHeight: '1rem'
                              }}>
                            {order.payment?.amount !== undefined
                                ? `${order.payment.amount} ${order.currency}`
                                : ''}
                        </span>
                    </div>
                </div>
                <div className={'fc'}>
                    <OrderStatus order={order}/>
                    <span>Платежная система: {order.payment_system}</span>
                    <span>
                        <span>Создан </span>
                        <span>
                            {moment(order.created_at).calendar().charAt(0).toLowerCase() +
                                moment(order.created_at).calendar().slice(1)}
                        </span>
                    </span>
                </div>
                <div className={'fc'}>
                    {order.consultation_language && <span>Язык: {order.consultation_language.value}</span>}
                    {order.consultation_communication_type && <span>
                            Коммуникация: {communicationTypeMapping[order.consultation_communication_type.value]}
                    </span>}
                    {order.psychologist_skills && <div className={'fc mb-1'}>
                        <span>Запрос:</span>
                        <div className={'ps-2 fc'}>
                            {order.psychologist_skills.map(skill => (
                                <div key={skill.id}>{skill.name}</div>
                            ))}
                        </div>
                    </div>}
                    {order.key && <FC>
                        <FRSC g={1} wrap>
                            <FR cls={'fs-5'} letterSpacing={'.08rem'}>КЛЮЧ АКТИВАЦИИ:</FR>
                            <FR px={1} pt={'2px'} cls={'hover-scale-1'} rounded={2}
                                onClick={() => {
                                    if (order.key) {
                                        copyToClipboard(order.key)
                                    }
                                }}
                                bg={plt.colors.secondary.colorful + '77'}>
                                {order.key}
                            </FR>
                        </FRSC>
                        <FR color={plt.text.primary50}>
                            Отправьте его тому кто будет активировать
                            сертификат и получать продукт(ы)
                        </FR>
                        <FR color={plt.text.primary50} g={.6}>
                            <span>Активировать сертификат он/она сможет по</span>
                            <a style={{color: plt.colors.secondary.colorful}} href={'/gift-certificate/activate'}
                               target={'_blank'}>ссылке</a>
                        </FR>
                    </FC>}
                </div>
                <OrderActions
                    extended={true}
                    order={order}
                    onSomeUpdatingOrderAction={setOrder}
                    setLoading={setIsActionLoading}/>
                {order.consultations && order.consultations.length > 0 && (
                    <div className={'fc gap-1 pb-2'}>
                        <span className={'fs-5 opacity-50'}>Консультации</span>
                        <ConsultationsWrapper
                            className={'px-3 pb-3 pt-1'}
                            style={{margin: '0 -0.7rem'}}
                            actionsEnable={false}
                            consultations={order.consultations}/>
                    </div>
                )}
            </div>
            {isActionLoading && (
                <FCCC pos={'absolute'} top={0} left={0} zIndex={8}
                      rounded={3} w={'100%'} h={'100%'}
                      cls={'backdrop-blur-10'}>
                    <CircularProgress size="3rem"/>
                </FCCC>
            )}
        </div>
    );
};

export default OrderDetail;
